import { fetchApi, fetchApiWithStatus } from "./api";
import { API_CONFIG, API_ENDPOINTS } from "../config/api";
import { pollJobUntilComplete } from "./jobsApi";
import type {
  ValidationIssue,
  ValidationFilters,
  UrlSummary,
  ValidationStats,
  ResolutionRequest,
  ScrapeResponse,
  ScrapeUrlResponse,
  ScrapeOptions,
} from "../types/validation";

const VALIDATION_BASE = `${API_CONFIG.baseUrl}/api/validation`;
const SCRAPERS_BASE = `${API_CONFIG.baseUrl}/api/scrapers`;

/** Server ValidationIssue shape (OpenAPI /api/validation): id, severity, source, status, etc. */
type ServerValidationIssue = Record<string, unknown> & {
  id: number;
  fingerprint?: string;
  /** New API: scraper source (tnvc, darq, steele, nve) */
  source?: string;
  /** New API: who generated the issue (agent, automated, llm_verification, etc.) — was `source` in old API */
  issue_source?: string;
  severity?: string;
  run_id?: number;
  log_id?: number | null;
  message?: string;
  field_name?: string | null;
  scope?: string;
  validation_type?: string;
  url?: string | null;
  product_id?: number | null;
  variant_id?: number | null;
  external_variant_id?: string | null;
  status?: string;
  resolution_type?: string | null;
  resolution_notes?: string | null;
  resolved_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

function normalizeIssue(raw: ServerValidationIssue): ValidationIssue {
  const severity = raw.severity ?? raw.issue_type ?? raw.level;
  const level = (
    severity === "error" || severity === "warning" || severity === "info"
      ? severity === "info"
        ? "warning"
        : severity
      : "warning"
  ) as ValidationIssue["level"];
  const status =
    raw.status === "resolved" ||
    raw.status === "pending" ||
    raw.status === "superseded"
      ? "addressed"
      : "unresolved";
  const created = typeof raw.created_at === "string" ? raw.created_at : "";

  const issue: ValidationIssue = {
    id: raw.id,
    log_id: raw.log_id != null ? String(raw.log_id) : String(raw.id),
    issue_index: 0,
    level,
    validation_type:
      typeof raw.validation_type === "string"
        ? raw.validation_type
        : String(raw.validation_type ?? ""),
    ...(raw.field_name != null && { field_name: String(raw.field_name) }),
    message: String(raw.message ?? ""),
    // New API splits source into issue_source (agent/automated/etc.) and source (scraper).
    // Fall back to raw.source for old API responses that don't have issue_source.
    source:
      ((raw.issue_source ?? raw.source) as ValidationIssue["source"]) ??
      "automated",
    scope: (raw.scope as ValidationIssue["scope"]) ?? "product",
    url: typeof raw.url === "string" ? raw.url : "",
    scraped_at: created,
    status,
  };
  if (raw.product_id != null) issue.product_id = String(raw.product_id);
  if (raw.variant_id != null) issue.variant_id = String(raw.variant_id);
  if (raw.external_variant_id != null)
    issue.external_variant_id = raw.external_variant_id;
  // Only set scraper_source when the new API is in use (issue_source present means source = scraper source)
  if (raw.issue_source != null && raw.source != null)
    issue.scraper_source = raw.source;
  if (typeof raw.resolved_at === "string") issue.addressed_at = raw.resolved_at;
  const resType = raw.resolution_type as
    | ValidationIssue["resolution_type"]
    | undefined;
  if (resType != null) issue.resolution_type = resType;
  if (raw.resolution_notes != null)
    issue.resolution_notes = raw.resolution_notes;
  return issue;
}

/** Matches server `IssueSource` enum — sent as `issue_source` (not scraper id). */
const ISSUE_PIPELINE_SOURCE_IDS = new Set([
  "automated",
  "llm_verification",
  "agent_verification",
]);

function appendSourceQueryParam(
  params: URLSearchParams,
  source: string | undefined,
) {
  if (!source) return;
  if (ISSUE_PIPELINE_SOURCE_IDS.has(source)) {
    params.append("issue_source", source);
  } else {
    params.append("nvg_source_id", source);
  }
  params.append("status", "unresolved");
}

export async function listIssues(
  filters: ValidationFilters = {},
): Promise<ValidationIssue[]> {
  const params = new URLSearchParams();

  appendSourceQueryParam(params, filters.source);
  if (filters.level) params.append("severity", filters.level);
  if (filters.status)
    params.append(
      "status",
      filters.status === "addressed" ? "resolved" : filters.status,
    );
  if (filters.validation_type)
    params.append("validation_type", filters.validation_type);
  if (filters.scope) params.append("scope", filters.scope);
  if (filters.search) params.append("search", filters.search);
  if (filters.product_id) params.append("product_id", filters.product_id);
  if (filters.variant_id) params.append("variant_id", filters.variant_id);
  const limit = filters.limit ?? API_CONFIG.defaultPageSize;
  const offset = filters.offset ?? 0;
  params.append("limit", String(Math.min(limit, API_CONFIG.maxPageSize)));
  params.append("offset", String(offset));

  if (filters.url) {
    const matchUrl = filters.url.trim();
    params.set("limit", String(API_CONFIG.maxPageSize));
    params.set("offset", "0");
    const data = await fetchApi<ServerValidationIssue[]>(
      `${VALIDATION_BASE}/issues?${params.toString()}`,
    );
    const full = Array.isArray(data) ? data : [];
    const filtered = full.filter(
      (i) => (i.url ?? "") === matchUrl || (i.url ?? "").includes(matchUrl),
    );
    const list = filtered.slice(offset, offset + limit);
    return list.map(normalizeIssue);
  }

  const url = `${VALIDATION_BASE}/issues?${params.toString()}`;
  const data = await fetchApi<ServerValidationIssue[]>(url);
  const list = Array.isArray(data) ? data : [];
  return list.map(normalizeIssue);
}

export async function getIssuesByUrl(
  url: string,
  params: { limit?: number; offset?: number } = {},
): Promise<ValidationIssue[]> {
  const limit = params.limit ?? 100;
  const offset = params.offset ?? 0;
  const all = await listIssues({ limit, offset });
  const filtered = all.filter((i) => i.url && i.url === url);
  return filtered;
}

export async function getUrlsSummary(
  params: { source?: string; limit?: number; offset?: number } = {},
): Promise<UrlSummary[]> {
  const limit = params.limit ?? 200;
  const offset = params.offset ?? 0;
  const filters: ValidationFilters = { limit, offset };
  if (params.source)
    filters.source = params.source as ValidationIssue["source"];
  const issues = await listIssues(filters);
  const byUrl = new Map<
    string,
    { source: ValidationIssue["source"]; issues: ValidationIssue[] }
  >();
  for (const i of issues) {
    const u = i.url || "";
    if (!u) continue;
    const existing = byUrl.get(u);
    if (!existing) {
      byUrl.set(u, { source: i.source, issues: [i] });
    } else {
      existing.issues.push(i);
    }
  }
  const summaries: UrlSummary[] = [];
  for (const [url, { source, issues: list }] of byUrl) {
    const error_count = list.filter((i) => i.level === "error").length;
    const warning_count = list.filter((i) => i.level === "warning").length;
    const unresolved_count = list.filter(
      (i) => i.status === "unresolved",
    ).length;
    const last = list
      .map((i) => i.scraped_at)
      .filter(Boolean)
      .sort()
      .pop();
    summaries.push({
      url,
      source,
      total_issues: list.length,
      error_count,
      warning_count,
      unresolved_count,
      last_scraped_at: last ?? "",
    });
  }
  return summaries;
}

export async function getStats(
  filters: ValidationFilters = {},
): Promise<ValidationStats> {
  const issues = await listIssues({
    ...filters,
    limit: API_CONFIG.maxPageSize,
    offset: 0,
  });
  const by_type: Record<string, number> = {};
  const by_scope: Record<string, number> = {};
  const by_level: Record<string, number> = { error: 0, warning: 0 };
  const by_status: Record<string, number> = { unresolved: 0, addressed: 0 };
  const by_source: Record<string, number> = {};

  for (const i of issues) {
    by_type[i.validation_type] = (by_type[i.validation_type] ?? 0) + 1;
    by_scope[i.scope] = (by_scope[i.scope] ?? 0) + 1;
    by_level[i.level] = (by_level[i.level] ?? 0) + 1;
    by_status[i.status] = (by_status[i.status] ?? 0) + 1;
    by_source[i.source] = (by_source[i.source] ?? 0) + 1;
  }

  return {
    total_issues: issues.length,
    error_count: by_level.error ?? 0,
    warning_count: by_level.warning ?? 0,
    unresolved_count: by_status.unresolved ?? 0,
    addressed_count: by_status.addressed ?? 0,
    by_type,
    by_scope,
    by_level,
    by_status,
    by_source,
  };
}

export async function getIssuesCount(
  filters: {
    source?: string;
    status?: string;
  } = {},
): Promise<{ count: number }> {
  const params = new URLSearchParams();
  appendSourceQueryParam(params, filters.source);
  return fetchApi<{ count: number }>(
    `${VALIDATION_BASE}/issues/count?${params.toString()}`,
  );
}

export async function addressIssue(
  issueId: number,
  _issueIndex: number,
  _issueType: string,
  resolution: ResolutionRequest,
): Promise<void> {
  const url = `${VALIDATION_BASE}/issues/${issueId}/resolve`;
  await fetchApi<void>(url, {
    method: "POST",
    body: JSON.stringify({
      resolution_type: resolution.resolution_type,
      resolution_notes: resolution.resolution_notes ?? undefined,
    }),
  });
}

/** New API does not support unaddressing; this is a no-op for compatibility. */
export async function unaddressIssue(
  _logId: string,
  _issueIndex: number,
  _issueType: string,
): Promise<void> {
  return Promise.resolve();
}

export async function getProductIssues(
  productId: string,
  params: {
    include_variants?: boolean;
    limit?: number;
    offset?: number;
    /** Filter by resolution status (e.g. 'unresolved' for open issues only). */
    status?: string;
  } = {},
): Promise<ValidationIssue[]> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined)
    searchParams.append("limit", String(params.limit));
  if (params.offset !== undefined)
    searchParams.append("offset", String(params.offset));
  if (params.status) searchParams.append("status", params.status);

  const url = `${VALIDATION_BASE}/products/${productId}/issues${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
  const data = await fetchApi<ServerValidationIssue[]>(url);
  const list = Array.isArray(data) ? data : [];
  return list.map(normalizeIssue);
}

/** Fetch validation issues for a variant. */
export async function getVariantIssues(
  variantId: string,
  params: { limit?: number; offset?: number; status?: string } = {},
): Promise<ValidationIssue[]> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined)
    searchParams.append("limit", String(params.limit));
  if (params.offset !== undefined)
    searchParams.append("offset", String(params.offset));
  if (params.status) searchParams.append("status", params.status);

  const url = `${VALIDATION_BASE}/variants/${variantId}/issues${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
  const data = await fetchApi<ServerValidationIssue[]>(url);
  const list = Array.isArray(data) ? data : [];
  return list.map(normalizeIssue);
}

/**
 * Start a full scrape for a source. Returns 202 with job_id; polls until complete, then returns result.
 *
 * Mirrors POST /api/scrapers/scrape which accepts:
 * - source (query, required)
 * - dry_run (query, optional)
 * - replay_run_id (query, optional; not yet exposed here)
 */
export async function startScrape(
  source: string,
  options: ScrapeOptions = {},
): Promise<ScrapeResponse> {
  const params = new URLSearchParams();
  if (options.dry_run) params.append("dry_run", "true");

  const url = `${SCRAPERS_BASE}/scrape?source=${encodeURIComponent(source)}${
    params.toString() ? `&${params.toString()}` : ""
  }`;
  const { status, data } = await fetchApiWithStatus(url, { method: "POST" });

  if (status === 202) {
    const body = data as { job_id?: string };
    const jobId = body?.job_id;
    if (!jobId) throw new Error("Missing job_id in 202 response");
    const pollOptions: {
      onStatus?: (status: "queued" | "in_progress") => void;
    } = {};
    if (options.onStatus) {
      pollOptions.onStatus = (s: "queued" | "in_progress") =>
        options.onStatus?.(s);
    }
    const result = await pollJobUntilComplete<ScrapeResponse>(
      jobId,
      pollOptions,
    );
    // Preserve the originating job id so UIs can open the jobs viewer.
    return { ...result, job_id: jobId };
  }

  return data as ScrapeResponse;
}

/**
 * Scrape a single URL. Returns 202 with job_id; polls until complete, then returns result.
 */
export async function scrapeUrl(
  url: string,
  options: ScrapeOptions = {},
): Promise<ScrapeUrlResponse> {
  const params = new URLSearchParams();
  if (options.dry_run) params.append("dry_run", "true");

  const endpoint = `${SCRAPERS_BASE}/scrape/url?url=${encodeURIComponent(url)}${params.toString() ? `&${params.toString()}` : ""}`;
  const { status, data } = await fetchApiWithStatus(endpoint, {
    method: "POST",
  });

  if (status === 202) {
    const body = data as { job_id?: string };
    const jobId = body?.job_id;
    if (!jobId) throw new Error("Missing job_id in 202 response");
    const pollOptions: {
      onStatus?: (status: "queued" | "in_progress") => void;
    } = {};
    if (options.onStatus) {
      pollOptions.onStatus = (s: "queued" | "in_progress") =>
        options.onStatus?.(s);
    }
    const result = await pollJobUntilComplete<ScrapeUrlResponse>(
      jobId,
      pollOptions,
    );
    // Preserve the originating job id so UIs can open the jobs viewer.
    return { ...result, job_id: jobId };
  }

  return data as ScrapeUrlResponse;
}

/** Fetch a self-contained batched validation prompt with pre-loaded HTML and scraped data. */
export async function getBatchedAgentPrompt(options: {
  product_id: number;
  variant_ids: number[];
  use_group_review?: boolean;
}): Promise<string> {
  const params = new URLSearchParams();
  params.set("product_id", String(options.product_id));
  for (const id of options.variant_ids) {
    params.append("variant_ids", String(id));
  }
  if (options.use_group_review === true) params.set("use_group_review", "true");
  const url = `${VALIDATION_BASE}/agent/batched-prompt?${params.toString()}`;
  const data = await fetchApi<{ prompt?: string } | string>(url);
  if (typeof data === "string") return data;
  if (data && typeof (data as { prompt?: string }).prompt === "string")
    return (data as { prompt: string }).prompt;
  return JSON.stringify(data);
}

/** Fetch the agent validation prompt for a product and/or variant. */
export async function getAgentPrompt(options: {
  product_id?: number;
  variant_id?: number;
  /** When true, prompt instructs the agent to call the group review endpoint on a clean pass. */
  use_group_review?: boolean;
}): Promise<string> {
  const params = new URLSearchParams();
  if (options.product_id != null)
    params.set("product_id", String(options.product_id));
  if (options.variant_id != null)
    params.set("variant_id", String(options.variant_id));
  if (options.use_group_review === true) params.set("use_group_review", "true");
  const url = params.toString()
    ? `${API_ENDPOINTS.validationAgentPrompt}?${params.toString()}`
    : API_ENDPOINTS.validationAgentPrompt;
  const data = await fetchApi<{ prompt?: string } | string>(url);
  if (typeof data === "string") return data;
  if (data && typeof (data as { prompt?: string }).prompt === "string")
    return (data as { prompt: string }).prompt;
  return JSON.stringify(data);
}
